import React, { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import cx from 'classnames';
import messages from 'common/messages/messages';
import invoiceMessages from 'common/components/InvoiceOperating/messages';
import { Button, DialogComponent, NumberTextField } from 'common/components';
import { defaultPriceNumberProps } from 'common/components/NumberController/NumberController';
import inputLabels from 'common/messages/inputLabels';
import { formatNumberToPrice } from 'common/utils';

interface IAddTipsModal {
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (tips: number) => void;
  totalAmount: number;
  tips: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    backgroundColor: theme.palette.secondary.light,
    padding: '3.5px 5.17px',
    borderRadius: '3px',
    '&:hover': {},
  },
  buttonText: {
    display: 'flex',
    '& span': { color: theme.palette.text.secondary },
  },
  percentage: { fontWeight: 700, marginRight: '2px' },
  buttonWrapper: {
    marginTop: '12px',
    '& .MuiButton-root:not(:last-child)': { marginRight: '12px' },
  },
  selected: {
    backgroundColor: theme.palette.primary.dark,
    '&:not(:hover) p, &:not(:hover) span': {
      color: theme.palette.background.paper,
    },
  },
}));

const AddTipsModal: FC<IAddTipsModal> = ({
  onClose,
  onSubmit,
  isLoading = false,
  totalAmount,
  tips,
}) => {
  const totalWithoutTips = totalAmount - tips;
  const maxTips = 9999.99 - totalWithoutTips;
  const intl = useIntl();
  const classes = useStyles();
  const [amountToPay, setAmountToPay] = useState<number>(tips);
  let error: string | undefined;

  const tip5 = +((totalWithoutTips * 5) / 100).toFixed(2);
  const tip10 = +((totalWithoutTips * 10) / 100).toFixed(2);
  const tip15 = +((totalWithoutTips * 15) / 100).toFixed(2);

  if (amountToPay > maxTips) {
    error = intl.formatMessage(invoiceMessages.maxTips, { maxTips });
  }

  const handleSubmit = () => {
    onSubmit(amountToPay);
  };

  return (
    <DialogComponent
      size="xs"
      title={intl.formatMessage(messages.addTips)}
      cancelBtnTitle={intl.formatMessage(messages.cancelBtn)}
      submitBtnTitle={intl.formatMessage(messages.addBtn)}
      isOpen
      onClose={onClose}
      disableFullScreenBehavior
      loading={isLoading}
      onSubmit={handleSubmit}
      disabled={!!error}
    >
      <>
        <NumberTextField
          fullWidth
          variant="outlined"
          onChange={value => setAmountToPay(Number(value))}
          label={intl.formatMessage(inputLabels.amountToPay)}
          value={amountToPay}
          numberFormatProps={defaultPriceNumberProps}
          error={!!error}
          helperText={error || undefined}
        />

        <Box className={classes.buttonWrapper}>
          <Button
            variant="contained"
            size="medium"
            className={cx(classes.button, {
              [classes.selected]: amountToPay !== 0 && amountToPay === tip5,
            })}
            onClick={() => setAmountToPay(tip5)}
          >
            <Typography className={classes.buttonText}>
              <Typography className={classes.percentage}>5%</Typography>
              <Typography component="span">{`($${formatNumberToPrice(tip5)})`}</Typography>
            </Typography>
          </Button>

          <Button
            variant="contained"
            size="medium"
            className={cx(classes.button, {
              [classes.selected]: amountToPay !== 0 && amountToPay === tip10,
            })}
            onClick={() => setAmountToPay(tip10)}
          >
            <Typography className={classes.buttonText}>
              <Typography className={classes.percentage}>10%</Typography>
              <Typography component="span">{`($${formatNumberToPrice(tip10)})`}</Typography>
            </Typography>
          </Button>

          <Button
            variant="contained"
            size="medium"
            className={cx(classes.button, {
              [classes.selected]: amountToPay !== 0 && amountToPay === tip15,
            })}
            onClick={() => setAmountToPay(tip15)}
          >
            <Typography className={classes.buttonText}>
              <Typography className={classes.percentage}>15%</Typography>
              <Typography component="span">{`($${formatNumberToPrice(tip15)})`}</Typography>
            </Typography>
          </Button>
        </Box>
      </>
    </DialogComponent>
  );
};

export default AddTipsModal;
